import { useState, useEffect, useRef } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Check, ChevronRight, PenLine, Sparkles } from 'lucide-react';
import OrbisSpinner from '@/components/ui/orbis-spinner';

/* ── Deterministic task list ── */
const TASKS = [
  { label: 'Reading approved structure and brand voice', duration: 2000 },
  { label: 'Crafting headline strategy per section', duration: 4000 },
  { label: 'Writing body copy and key messaging', duration: 6000 },
  { label: 'Generating CTA labels and placement', duration: 4000 },
  { label: 'Building content items (stats, features, testimonials)', duration: 5000 },
  { label: 'Writing image art direction per section', duration: 4000 },
  { label: 'Classifying asset types (photo vs UI asset)', duration: 3000 },
  { label: 'Enforcing brand voice consistency', duration: 3000 },
  { label: 'Finalizing content for review', duration: null },
];

/* ── Narrative messages ── */
function buildNarrativeMessages(brief, structure) {
  const company = brief?.company_name || 'your project';
  const sectionCount = (structure?.pages || []).reduce((sum, p) => sum + (p.sections?.length || 0), 0);

  return [
    `I have your approved structure — ${sectionCount} sections across the site. Now writing the copy and creative direction for each one.`,
    `Starting with the hero. For ${company}, the headline needs to hook in under 3 seconds. Making it sharp and specific.`,
    `Writing body copy for each section. Every paragraph answers the visitor's core question for that section.`,
    `Generating CTAs that align with your conversion strategy. Primary action front and center, secondary below the fold.`,
    `Creating image art direction — describing the exact composition, lighting, and mood for each visual.`,
    `Content complete. Packaging everything for your review.`,
  ];
}

/* ── Typewriter hook ── */
function useTypewriter(text, speed = 35, active = false) {
  const [displayed, setDisplayed] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!active || !text) { setDisplayed(''); setDone(false); return; }
    setDisplayed('');
    setDone(false);
    let i = 0;
    const interval = setInterval(() => {
      i++;
      setDisplayed(text.slice(0, i));
      if (i >= text.length) { clearInterval(interval); setDone(true); }
    }, speed);
    return () => clearInterval(interval);
  }, [text, speed, active]);

  return { displayed, done };
}

/* ── Task Item ── */
const TaskItem = ({ label, status, index, reducedMotion }) => (
  <motion.div
    initial={reducedMotion ? {} : { opacity: 0, x: -8 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{ delay: index * 0.04, duration: 0.25 }}
    className="flex items-center gap-3 py-2 px-3 rounded-lg transition-colors"
    style={{
      background: status === 'active' ? 'var(--orbis-s1)' : 'transparent',
      border: status === 'active' ? '1px solid var(--orbis-border-md)' : '1px solid transparent',
    }}
  >
    <div className="w-5 h-5 flex items-center justify-center shrink-0">
      {status === 'pending' && (
        <div className="w-2 h-2 rounded-full" style={{ background: 'var(--orbis-s3)' }} />
      )}
      {status === 'active' && (
        <OrbisSpinner size={16} color="#B8CCDF" />
      )}
      {status === 'done' && (
        <motion.div
          initial={reducedMotion ? false : { scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', stiffness: 400, damping: 20 }}
          className="w-5 h-5 rounded-full flex items-center justify-center"
          style={{ background: 'rgba(52,211,153,0.12)' }}
        >
          <Check className="w-3 h-3" style={{ color: 'var(--orbis-positive)' }} />
        </motion.div>
      )}
    </div>
    <span style={{
      fontSize: '13px',
      fontFamily: 'var(--orbis-font)',
      color: status === 'active' ? 'var(--orbis-t1)' : status === 'done' ? 'var(--orbis-t3)' : 'var(--orbis-t4)',
      fontWeight: status === 'active' ? 500 : 400,
    }}>
      {label}
    </span>
  </motion.div>
);

/* ── Narrative Message ── */
const NarrativeMessage = ({ text, isActive, isDone }) => {
  const { displayed, done } = useTypewriter(text, 35, isActive || isDone);

  return (
    <div className="flex gap-3 py-3 px-4 rounded-lg" style={{
      background: isActive ? 'var(--orbis-s1)' : 'transparent',
      border: isActive ? '1px solid var(--orbis-border-md)' : '1px solid transparent',
    }}>
      <div className="w-6 h-6 rounded-md flex items-center justify-center shrink-0 mt-0.5" style={{
        background: isActive ? 'rgba(184,204,223,0.1)' : 'transparent',
      }}>
        <PenLine className="w-3.5 h-3.5" style={{ color: isActive ? 'var(--orbis-blue)' : 'var(--orbis-t4)' }} />
      </div>
      <p style={{
        fontSize: '14px',
        lineHeight: 1.7,
        color: isActive ? 'var(--orbis-t1)' : 'var(--orbis-t3)',
        minHeight: '1.7em',
      }}>
        {isDone ? text : displayed}
        {isActive && !done && <span className="animate-pulse" style={{ color: 'var(--orbis-blue)' }}>|</span>}
      </p>
    </div>
  );
};

/* ── Main ContentTransition Component ── */
export default function ContentTransition({ brief, structure, designSystem, isComplete, onContinue }) {
  const reducedMotion = useReducedMotion();
  const [taskStatuses, setTaskStatuses] = useState(() => TASKS.map(() => 'pending'));
  const [activeMessageIdx, setActiveMessageIdx] = useState(-1);
  const [completedMessages, setCompletedMessages] = useState(new Set());
  const [elapsed, setElapsed] = useState(0);
  const [showContinue, setShowContinue] = useState(false);
  const startTime = useRef(Date.now());
  const feedRef = useRef(null);
  const narrativeMessages = useRef(buildNarrativeMessages(brief, structure)).current;
  const completedRef = useRef(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startTime.current) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const prev = document.title;
    document.title = 'Writing content... · Orbis';
    return () => { document.title = prev; };
  }, []);

  // Task progression
  useEffect(() => {
    let cumulative = 0;
    const timers = [];

    TASKS.forEach((task, idx) => {
      if (idx === TASKS.length - 1) return;
      timers.push(setTimeout(() => {
        setTaskStatuses(prev => { const next = [...prev]; next[idx] = 'active'; return next; });
      }, cumulative));
      cumulative += task.duration;
      timers.push(setTimeout(() => {
        setTaskStatuses(prev => { const next = [...prev]; next[idx] = 'done'; return next; });
      }, cumulative));
    });

    timers.push(setTimeout(() => {
      setTaskStatuses(prev => { const next = [...prev]; next[TASKS.length - 1] = 'active'; return next; });
    }, cumulative));

    return () => timers.forEach(clearTimeout);
  }, []);

  // Narrative progression
  useEffect(() => {
    const timers = [];
    let cumulative = 1500;

    narrativeMessages.forEach((_, idx) => {
      timers.push(setTimeout(() => setActiveMessageIdx(idx), cumulative));
      const typingTime = narrativeMessages[idx].length * 35;
      cumulative += typingTime + 1500;
      timers.push(setTimeout(() => {
        setCompletedMessages(prev => new Set([...prev, idx]));
      }, cumulative - 1500));
    });

    return () => timers.forEach(clearTimeout);
  }, [narrativeMessages]);

  // Fast-forward on complete
  useEffect(() => {
    if (!isComplete || completedRef.current) return;
    completedRef.current = true;
    document.title = 'Content ready · Orbis';

    const fastForward = async () => {
      for (let i = 0; i < TASKS.length; i++) {
        setTaskStatuses(prev => {
          if (prev[i] !== 'done') { const next = [...prev]; next[i] = 'done'; return next; }
          return prev;
        });
        await new Promise(r => setTimeout(r, 200));
      }
      setTimeout(() => setShowContinue(true), 400);
    };
    fastForward();
  }, [isComplete]);

  useEffect(() => {
    if (feedRef.current) feedRef.current.scrollTop = feedRef.current.scrollHeight;
  }, [activeMessageIdx, completedMessages]);

  const progressPercent = isComplete ? 100 : Math.min(95, (elapsed / 40) * 95);
  const completedCount = taskStatuses.filter(s => s === 'done').length;
  const formatElapsed = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  return (
    <div className="flex flex-col w-full" style={{ height: 'calc(100vh - 64px)', background: 'var(--orbis-bg)' }}>
      <div className="flex flex-1 min-h-0">

      {/* Left Rail */}
      <div className="w-[400px] shrink-0 flex flex-col" style={{ borderRight: '1px solid var(--orbis-border-md)' }}>
        <div style={{ padding: '16px 16px 12px' }}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <OrbisSpinner size={20} color="#B8CCDF" />
              <span style={{ fontSize: '13px', fontWeight: 500, color: 'var(--orbis-t1)', fontFamily: 'var(--orbis-font)' }}>
                Writing Content
              </span>
            </div>
            <span style={{ fontSize: '11px', fontFamily: 'var(--orbis-mono)', color: 'var(--orbis-t3)' }}>
              {completedCount}/{TASKS.length}
            </span>
          </div>
          <div className="w-full h-[3px] rounded-full" style={{ background: 'var(--orbis-s2)' }}>
            <motion.div
              className="h-full rounded-full"
              style={{ background: isComplete ? 'var(--orbis-positive)' : 'var(--orbis-blue)' }}
              animate={{ width: `${progressPercent}%` }}
              transition={{ duration: isComplete ? 0.3 : 1, ease: 'linear' }}
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-2">
          {TASKS.map((task, idx) => (
            <TaskItem key={idx} label={task.label} status={taskStatuses[idx]} index={idx} reducedMotion={reducedMotion} />
          ))}
        </div>

        <div className="px-4 py-3" style={{ borderTop: '1px solid var(--orbis-border-md)' }}>
          <div className="flex items-center justify-between">
            <span style={{ fontSize: '12px', fontFamily: 'var(--orbis-mono)', color: 'var(--orbis-t3)' }}>
              {formatElapsed(elapsed)} elapsed
            </span>
            <span style={{ fontSize: '11px', color: 'var(--orbis-t4)' }}>Usually 30–60s</span>
          </div>
        </div>
      </div>

      {/* Center — AI Narrative Feed */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="flex items-center justify-between" style={{ padding: '16px 20px', borderBottom: '1px solid var(--orbis-border-md)' }}>
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'var(--orbis-s1)', border: '1px solid var(--orbis-border-md)' }}>
              <PenLine className="w-3.5 h-3.5" style={{ color: 'var(--orbis-blue)' }} />
            </div>
            <span style={{ fontSize: '15px', fontWeight: 500, color: 'var(--orbis-t1)', fontFamily: 'var(--orbis-font)' }}>
              AI Content Director
            </span>
          </div>
          <span style={{ fontSize: '13px', color: 'var(--orbis-t3)' }}>
            {brief?.company_name || ''}
          </span>
        </div>

        <div ref={feedRef} className="flex-1 overflow-y-auto" style={{ padding: '12px 16px' }}>
          <div className="space-y-2">
            {narrativeMessages.map((msg, idx) => {
              if (idx > activeMessageIdx && !completedMessages.has(idx)) return null;
              return (
                <NarrativeMessage
                  key={idx}
                  text={msg}
                  isActive={idx === activeMessageIdx && !completedMessages.has(idx)}
                  isDone={completedMessages.has(idx)}
                />
              );
            })}

            {showContinue && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex gap-3 py-3 px-4 rounded-lg mt-2"
                style={{ background: 'rgba(52,211,153,0.06)', border: '1px solid rgba(52,211,153,0.15)' }}
              >
                <div className="w-6 h-6 rounded-md flex items-center justify-center shrink-0 mt-0.5" style={{ background: 'rgba(52,211,153,0.12)' }}>
                  <Check className="w-3.5 h-3.5" style={{ color: 'var(--orbis-positive)' }} />
                </div>
                <p style={{ fontSize: '14px', lineHeight: 1.7, color: 'var(--orbis-t1)' }}>
                  Content and art direction ready. Review your copy before generating assets.
                </p>
              </motion.div>
            )}

            {!isComplete && activeMessageIdx >= narrativeMessages.length - 1 && completedMessages.has(narrativeMessages.length - 1) && (
              <div className="flex items-center gap-3 py-4 px-4">
                <OrbisSpinner size={20} color="#B8CCDF" />
                <span style={{ fontSize: '13px', color: 'var(--orbis-t3)' }}>
                  {elapsed > 90 ? 'AI is still thinking — complex content takes longer...' : elapsed > 45 ? 'Writing detailed content — almost there...' : 'Finishing up...'}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      </div>

      {/* Bottom Bar */}
      <div className="magic-bottom-bar">
        <div style={{ width: '180px' }} />
        <span style={{ fontSize: '12px', color: 'var(--orbis-t4)', fontFamily: 'var(--orbis-mono)' }}>
          {showContinue ? (
            <span className="flex items-center gap-2">
              <Check className="w-3.5 h-3.5" style={{ color: 'var(--orbis-positive)' }} />
              Content ready
            </span>
          ) : (
            <span className="flex items-center gap-2">
              <OrbisSpinner size={14} color="#B8CCDF" />
              Writing content
            </span>
          )} · Step 4 of 8
        </span>
        <div className="orbis-cta-ring rounded-full p-0.5">
          <button
            onClick={showContinue ? onContinue : undefined}
            disabled={!showContinue}
            className="orbis-cta flex items-center justify-center gap-2 transition-all rounded-full"
            style={{
              width: '220px', height: '44px',
              opacity: showContinue ? 1 : 0.3,
              fontSize: '13px', fontWeight: 500,
              cursor: showContinue ? 'pointer' : 'not-allowed',
              fontFamily: 'var(--orbis-font)',
            }}
          >
            {showContinue ? (
              <>Review Content <ChevronRight className="w-4 h-4" /></>
            ) : (
              <>Writing...</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
